import { Button } from "@devhop/ui";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { useRef } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  useCreateJD,
  useJDDetailQuery,
  useJDVersion,
  useUpdateJD,
} from "../api/queries";
import { JDForm, type JDFormRef } from "../ui/JDForm";

export function JDCreatePage() {
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;
  const isEdit = pathname.includes("/edit");

  // Extract ID from pathname for edit route
  const id = isEdit ? pathname.match(/\/jd\/([^/]+)\/edit/)?.[1] : undefined;
  // Get versionId from URL search params
  const searchParams = new URLSearchParams(routerState.location.search);
  const versionId = searchParams.get("versionId") || undefined;

  const nav = useNavigate();

  const canCreate = useActionPermission(["jd:create"]);
  const canUpdate = useActionPermission(["jd:update"]);
  const hasPermission = isEdit ? canUpdate : canCreate;

  const createJD = useCreateJD();
  const updateJD = useUpdateJD(id ?? "");
  const jdDetail = useJDDetailQuery(id ?? "");
  const versionData = useJDVersion(id ?? "", versionId ?? "");

  // Use version data if available, otherwise use current JD data
  const initialData =
    versionId && versionData.data ? versionData.data : jdDetail.data;

  const formRef = useRef<JDFormRef>(null);

  if (!hasPermission) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">
              {isEdit ? "ທ່ານບໍ່ມີສິດແກ້ໄຂ JD" : "ທ່ານບໍ່ມີສິດສ້າງ JD"}
            </p>
          </div>
        </Main>
      </>
    );
  }

  if (isEdit && (jdDetail.isLoading || (versionId && versionData.isLoading))) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">ກໍາລັງໂຫຼດ...</p>
          </div>
        </Main>
      </>
    );
  }

  if (isEdit && !initialData) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">ບໍ່ພົບ JD</p>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />

      <Main>
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">
              {isEdit ? (versionId ? "ແກ້ໄຂຈາກເວີຊັນ" : "ແກ້ໄຂ JD") : "ສ້າງ JD ໃໝ່"}
            </h2>
            <p className="text-muted-foreground">
              {isEdit
                ? versionId
                  ? "ແກ້ໄຂຈາກເວີຊັນເກົ່າ (ຈະສ້າງເວີຊັນໃໝ່ເມື່ອບັນທຶກ)"
                  : "ແກ້ໄຂຄຳອະທິບາຍຕໍາແໜ່ງ"
                : "ຕື່ມຂໍ້ມູນຄຳອະທິບາຍຕໍາແໜ່ງໃໝ່"}
            </p>
          </div>

          <Button
            variant="outline"
            onClick={() => {
              // If editing, go back to detail page, otherwise go to list
              if (isEdit && versionId) {
                nav({ to: `/app/jd/${id}` });
              } else {
                nav({ to: "/app/jd" });
              }
            }}
          >
            <ArrowLeftIcon className="size-4" />{" "}
            {isEdit && versionId ? "ກັບໄປລາຍລະອຽດ" : "ກັບໄປລາຍການ"}
          </Button>
        </div>

        <div className="rounded-lg border bg-card p-6">
          <JDForm
            ref={formRef}
            initialValues={isEdit ? initialData : undefined}
            onSubmit={async (vals) => {
              try {
                const formData = {
                  title: vals.title,
                  departmentId: vals.departmentId,
                  level: String(vals.level),
                  reportsToPositionId: vals.reportsToPositionId || undefined,
                  managesCount:
                    vals.managesCount !== null &&
                    vals.managesCount !== undefined
                      ? String(vals.managesCount)
                      : undefined,
                  status: String(vals.status),
                  responsibilities: vals.responsibilities as Array<{
                    description: string;
                    orderIndex: number;
                  }>,
                  routineTasks: vals.routineTasks as Array<{
                    description: string;
                    frequency:
                      | "daily"
                      | "weekly"
                      | "monthly"
                      | "quarterly"
                      | "annual";
                    orderIndex: number;
                  }>,
                  requirements: vals.requirements as Array<{
                    type:
                      | "experience"
                      | "skills"
                      | "knowledge"
                      | "education"
                      | "other";
                    description: string;
                    isRequired: boolean;
                    orderIndex: number;
                  }>,
                };

                if (isEdit) {
                  await updateJD.mutateAsync(
                    formData as unknown as Parameters<
                      typeof updateJD.mutateAsync
                    >[0],
                  );
                  // After update, go back to detail page
                  if (id) {
                    nav({ to: `/app/jd/${id}` });
                  } else {
                    nav({ to: "/app/jd" });
                  }
                } else {
                  await createJD.mutateAsync(
                    formData as unknown as Parameters<
                      typeof createJD.mutateAsync
                    >[0],
                  );
                  nav({ to: "/app/jd" });
                }
              } catch (error) {
                // Error is already handled by the fetcher and shown via toast
                console.error(
                  `Failed to ${isEdit ? "update" : "create"} JD:`,
                  error,
                );
              }
            }}
            submitting={isEdit ? updateJD.isPending : createJD.isPending}
          />
        </div>
      </Main>
    </>
  );
}
